import { Fragment, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '@artosera/ui';
import {
  mentionsDeLInscription,
  mentionsRegistrePoliceSchema,
  REGLES_REGISTRE_POLICE,
  type BrouillonRegistrePolice,
} from '@artosera/shared';
import { galerieALaction } from '../../auth/sujet.js';
import {
  useInscriptionRegistre,
  useMutationRegistre,
  registrePoliceApi,
  type InscriptionPresentee,
} from '../../api/registrePolice.js';
import { estErreurStepUpRequis, useTelechargerDocument } from '../../api/coffre.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { StepUpDialog } from '../coffre/StepUpDialog.js';
import { MentionsForm } from './MentionsForm.js';
import styles from './RegistrePolice.module.css';

function Mentions({ entree, voitPiece }: { entree: InscriptionPresentee; voitPiece: boolean }) {
  const { t, i18n } = useTranslation('regie');
  const label = (cle: string) => t(`registrePolice.${cle}`);
  const date = (valeur: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
      new Date(valeur),
    );
  const lignes = [
    ['dateAcquisition', date(entree.dateAcquisition)],
    ['modeAcquisition', label(`valeurs.${entree.modeAcquisition}`)],
    [
      'prixAchatCentimes',
      entree.prixAchatCentimes == null
        ? '—'
        : new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: entree.devise,
          }).format(entree.prixAchatCentimes / 100),
    ],
    ['devise', entree.devise],
    ['modeReglement', entree.modeReglement ? label(`valeurs.${entree.modeReglement}`) : '—'],
    ...Object.entries(entree.objet)
      .filter(([cle]) => !['oeuvreId', 'exemplaireId', 'photoMediaId'].includes(cle))
      .map(([cle, valeur]) => [`objet.${cle}`, valeur]),
    ['vendeur.qualite', label(`valeurs.${entree.vendeur.qualite}`)],
    ...Object.entries(entree.vendeur)
      .filter(([cle]) => !['contactId', 'qualite', 'adresse'].includes(cle))
      .map(([cle, valeur]) => [`vendeur.${cle}`, String(valeur)]),
    ...Object.entries(entree.vendeur.adresse).map(([cle, valeur]) => [
      `vendeur.adresse.${cle}`,
      valeur,
    ]),
  ];
  return (
    <>
      <dl>
        {lignes.map(([cle, valeur]) => (
          <Fragment key={cle}>
            <dt>{label(`champs.${cle}`)}</dt>
            <dd>{valeur || '—'}</dd>
          </Fragment>
        ))}
      </dl>
      {voitPiece && entree.piece && (
        <section>
          <h3>{label('piece')}</h3>
          <dl>
            {(['nature', 'numero', 'delivreeLe', 'autorite'] as const).map((cle) => (
              <Fragment key={cle}>
                <dt>{label(`champs.piece.${cle}`)}</dt>
                <dd>
                  {cle === 'delivreeLe' ? date(entree.piece!.delivreeLe) : entree.piece![cle]}
                </dd>
              </Fragment>
            ))}
          </dl>
        </section>
      )}
    </>
  );
}
export function InscriptionPage() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const numero = Number(useParams()['numero']);
  const naviguer = useNavigate();
  const peutLire = galerieALaction(galerie, 'registre-police:read');
  const voitPiece = galerieALaction(galerie, 'registre-police:rectifier');
  const peutInscrire = galerieALaction(galerie, 'registre-police:inscrire');
  const fiche = useInscriptionRegistre(galerieSlug, numero, peutLire);
  const [rectification, setRectification] = useState(false);
  const [sortieOuverte, setSortieOuverte] = useState(false);
  const [dateSortie, setDateSortie] = useState(new Date().toISOString().slice(0, 10));
  const [natureSortie, setNatureSortie] = useState<'vente' | 'restitution' | 'destruction'>(
    'vente',
  );
  const [factureRef, setFactureRef] = useState('');
  const [erreurMentions, setErreurMentions] = useState('');
  const [stepUpOuvert, setStepUpOuvert] = useState(false);
  const label = (cle: string) => t(`regie:registrePolice.${cle}`);
  const corriger = useMutationRegistre(
    galerieSlug,
    ({ brouillon, raison }: { brouillon: BrouillonRegistrePolice; raison: string }, appel) =>
      registrePoliceApi.rectifier(
        galerieSlug,
        numero,
        mentionsRegistrePoliceSchema.parse(brouillon),
        raison,
        appel,
      ),
  );
  const sortir = useMutationRegistre(galerieSlug, (_: void, appel) =>
    registrePoliceApi.sortir(
      galerieSlug,
      numero,
      { date: dateSortie, nature: natureSortie, ...(factureRef ? { factureRef } : {}) },
      appel,
    ),
  );
  const telecharger = useTelechargerDocument(galerieSlug);
  const inscription = fiche.data?.inscription;
  const courante = fiche.data?.rectifications.at(-1) ?? inscription;
  const erreur = fiche.error || corriger.error || sortir.error;
  async function ouvrirScan() {
    if (!voitPiece || !courante?.piece?.documentId) return;
    try {
      const resultat = await telecharger.mutateAsync({ id: courante.piece.documentId });
      ouvrirUrlSignee(resultat.telechargement.url);
    } catch (erreur) {
      if (estErreurStepUpRequis(erreur)) setStepUpOuvert(true);
    }
  }
  return (
    <section className={styles.page} data-ui="page">
      <header>
        <Link to={`/${galerieSlug}/registre-police`}>{label('titre')}</Link>
        <h1>{t('regie:registrePolice.inscriptionTitre', { numero })}</h1>
      </header>
      {!peutLire && <p role="alert">{label('refus')}</p>}
      {peutLire && fiche.isPending && <p>{t('commun:chargement')}</p>}
      {erreur && (
        <p role="alert">{estApiError(erreur) ? erreur.message : t('commun:erreurReseau')}</p>
      )}
      {peutLire && inscription && courante && (
        <>
          <article className={styles.fiche}>
            <p>
              {label(`natures.${inscription.nature}`)} · {inscription.auteur.nom} ·{' '}
              {new Intl.DateTimeFormat(i18n.language, {
                dateStyle: 'medium',
                timeZone: 'UTC',
              }).format(new Date(inscription.valideLe))}
            </p>
            <Mentions entree={courante} voitPiece={voitPiece} />
            {voitPiece && courante.piece?.documentId && (
              <>
                <Button onClick={() => void ouvrirScan()} disabled={telecharger.isPending}>
                  {label('ouvrirScan')}
                </Button>
                {telecharger.isError && !estErreurStepUpRequis(telecharger.error) && (
                  <p role="alert">
                    {estApiError(telecharger.error)
                      ? telecharger.error.message
                      : t('commun:erreurReseau')}
                  </p>
                )}
              </>
            )}
            <p>
              {label('empreinte')} : {inscription.empreinte}
            </p>
          </article>
          <section className={styles.fiche}>
            <h2>{label('rectifications')}</h2>
            {fiche.data!.rectifications.map((entree) => (
              <details key={entree.numero}>
                <summary>
                  {entree.numero} · {entree.auteur.nom} · {entree.raison}
                </summary>
                <Mentions entree={entree} voitPiece={voitPiece} />
              </details>
            ))}
          </section>
          {fiche.data!.sortie && (
            <section className={styles.fiche}>
              <h2>{label('sortie')}</h2>
              <p>
                {fiche.data!.sortie.sortie?.date.slice(0, 10)} ·{' '}
                {label(`valeurs.${fiche.data!.sortie.sortie?.nature}`)} ·{' '}
                {fiche.data!.sortie.sortie?.factureRef}
              </p>
            </section>
          )}
          {inscription.nature !== 'sortie' && (
            <div className={styles.actions}>
              {voitPiece && (
                <Button onClick={() => setRectification(!rectification)}>
                  {label('rectifier')}
                </Button>
              )}
              {peutInscrire && !fiche.data!.sortie && (
                <Button onClick={() => setSortieOuverte(!sortieOuverte)}>
                  {label('enregistrerSortie')}
                </Button>
              )}
            </div>
          )}
          {voitPiece && rectification && (
            <>
              {erreurMentions && <p role="alert">{erreurMentions}</p>}
              <MentionsForm
                key={courante.numero}
                initial={mentionsDeLInscription({
                  ...courante,
                  dateAcquisition: new Date(courante.dateAcquisition),
                  piece: courante.piece
                    ? { ...courante.piece, delivreeLe: new Date(courante.piece.delivreeLe) }
                    : undefined,
                })}
                galerieSlug={galerieSlug}
                voitPiece
                enCours={corriger.isPending}
                raisonRequise
                onEnregistrer={async (brouillon, raison) => {
                  setErreurMentions('');
                  if (!mentionsRegistrePoliceSchema.safeParse(brouillon).success) {
                    setErreurMentions(label('mentionsInvalides'));
                    return;
                  }
                  try {
                    const reponse = await corriger.mutateAsync({
                      brouillon,
                      raison: raison.trim(),
                    });
                    setRectification(false);
                    void naviguer(`/${galerieSlug}/registre-police/${reponse.inscription.cite}`);
                  } catch {
                    /* Les mentions restent disponibles pour correction. */
                  }
                }}
              />
            </>
          )}
          {peutInscrire && sortieOuverte && !fiche.data!.sortie && (
            <form
              className={styles.formulaire}
              onSubmit={(event) => {
                event.preventDefault();
                void sortir
                  .mutateAsync()
                  .then(() => setSortieOuverte(false))
                  .catch(() => undefined);
              }}
            >
              <Field htmlFor="sortie-date" label={label('dateSortie')}>
                <Input
                  id="sortie-date"
                  type="date"
                  required
                  value={dateSortie}
                  onChange={(event) => setDateSortie(event.target.value)}
                />
              </Field>
              <Field htmlFor="sortie-nature" label={label('natureSortie')}>
                <Select
                  id="sortie-nature"
                  value={natureSortie}
                  onValueChange={(valeur) => setNatureSortie(valeur as typeof natureSortie)}
                  options={REGLES_REGISTRE_POLICE.naturesSortie.valeur.map((valeur) => ({
                    value: valeur,
                    label: label(`valeurs.${valeur}`),
                  }))}
                />
              </Field>
              <Field htmlFor="sortie-facture" label={label('factureRef')}>
                <Input
                  id="sortie-facture"
                  value={factureRef}
                  onChange={(event) => setFactureRef(event.target.value)}
                />
              </Field>
              <Button type="submit" disabled={sortir.isPending || !dateSortie}>
                {label('enregistrerSortie')}
              </Button>
            </form>
          )}
        </>
      )}
      <StepUpDialog
        open={stepUpOuvert && voitPiece}
        onOpenChange={setStepUpOuvert}
        onVerifie={() => void ouvrirScan()}
      />
    </section>
  );
}
